import StyledTooltip from "@/components/styled-tooltip";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetProviderBreakdown } from "@/hooks/use-inboxplacement";
import { cn } from "@/lib/utils";
import { b2bProviders, b2cProviders } from "@/types/inboxPlacements.types";
import { Inbox, Info, MailWarning, OctagonAlert } from "lucide-react";
import { useMemo } from "react";
import { EmailPlacementChart } from "../placement-chart";
import { usePlacementContext } from "./placement-context";
import { EmailProvider } from "@/types/inboxPlacements.types";

const statsCards = [
  {
    key: "inbox",
    icon: Inbox,
    title: "Inbox",
    classNames: {
      stroke: "stroke-green-500 ",
      icon: "text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20",
    },
  },
  {
    key: "spam",
    icon: OctagonAlert,
    title: "Spam",
    classNames: {
      stroke: "stroke-red-500",
      icon: "text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/20",
    },
  },
  {
    key: "unreceived",
    icon: MailWarning,
    title: "Unreceived",
    classNames: {
      stroke: "stroke-yellow-500",
      icon: "text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20",
    },
  },
];

const PlacementOverallScore = () => {
  const { state, isNotCompleted, summary } = usePlacementContext();

  const { data: providerBreakdown, isLoading: providerBreakdownLoading } =
    useGetProviderBreakdown(state.params.email, state.params.testId);

  const statsCardsData = useMemo(() => {
    if (!providerBreakdown?.providers)
      return {
        inbox: {
          value: 0,
          outof: 0,
          percentage: 0,
        },
        spam: {
          value: 0,
          outof: 0,
          percentage: 0,
        },
        unreceived: {
          value: 0,
          outof: 0,
          percentage: 0,
        },
      };

    return {
      inbox: {
        value: summary.inbox.count,
        outof: summary.total,
        percentage: summary.inbox.percentage,
      },
      spam: {
        value: summary.spam.count,
        outof: summary.total,
        percentage: summary.spam.percentage,
      },
      unreceived: {
        value: summary.unreceived.count,
        outof: summary.total,
        percentage: summary.unreceived.percentage,
      },
    };
  }, [providerBreakdown?.providers, summary]);

  const chartData = useMemo(() => {
    if (!providerBreakdown?.providers) {
      return [];
    }

    // Create a combined priority map for both B2B and B2C providers
    // B2B providers come first, then B2C providers
    const allProviders = [...b2bProviders, ...b2cProviders];
    const providerPriorityMap = Object.fromEntries(
      allProviders.map((provider, index) => [provider, index])
    );

    // Since we're using B2B API but showing all providers, we need to show both B2B and B2C
    // Filter and sort all providers (both B2B and B2C)
    const allProviderData = providerBreakdown.providers
      .filter((provider) => {
        // Include both B2B and B2C providers
        return (
          b2bProviders.includes(provider.provider as EmailProvider) ||
          b2cProviders.includes(provider.provider as EmailProvider)
        );
      })
      .sort((a, b) => {
        const priorityA = providerPriorityMap[a.provider as EmailProvider] ?? 999;
        const priorityB = providerPriorityMap[b.provider as EmailProvider] ?? 999;
        return priorityA - priorityB;
      })
      .map((provider) => {
        return {
          month: provider.displayName,
          Inbox: Math.round(provider.inboxPercentage),
          Spam: Math.round(provider.spamPercentage),
          Unreceived: Math.round(provider.unreceivedPercentage),
          Total: provider.emailCount || 0,
        };
      });

    return allProviderData;
  }, [providerBreakdown?.providers]);

  return (
    <div>
      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-medium">Overall Result</h2>
          <StyledTooltip description="Shows the overall result of the test. This helps identify if the email was delivered to the inbox or spam folder.">
            <Info className="h-4 w-4 text-muted-foreground" />
          </StyledTooltip>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ">
          {providerBreakdownLoading ? (
            // Show skeleton cards while loading
            <OverviewCardSkeleton />
          ) : (
            statsCards.map((card, index) => (
              <Card key={index} className="relative">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div
                          className={cn(
                            "border-transparent rounded-lg bg-secondary size-8 flex items-center justify-center shrink-0",
                            card.classNames.icon
                          )}
                        >
                          <card.icon className="h-5 w-5" />
                        </div>
                        <span className="text-sm font-medium flex items-center gap-1">
                          {card.title}
                          <StyledTooltip
                            description={
                              card.title === "Inbox"
                                ? "Shows how many test emails were successfully delivered to the inbox folder."
                                : card.title === "Spam"
                                ? "Shows how many test emails were delivered to the spam or junk folder."
                                : "Shows how many test emails were not received at all."
                            }
                          >
                            <Info className="h-3.5 w-3.5 text-muted-foreground" />
                          </StyledTooltip>
                        </span>
                      </div>
                      <div className="space-y-1">
                        <div className="text-3xl font-bold">
                          {
                            statsCardsData[
                              card.key as keyof typeof statsCardsData
                            ]?.value
                          }
                          <span className="text-sm text-muted-foreground">
                            /
                            {
                              statsCardsData[
                                card.key as keyof typeof statsCardsData
                              ]?.outof
                            }
                          </span>
                        </div>
                      </div>
                    </div>

                    <ProgressCircle
                      percentage={
                        statsCardsData[card.key as keyof typeof statsCardsData]
                          ?.percentage
                      }
                      color={card.classNames.stroke}
                      className="size-20"
                      strokeWidth={10}
                    />
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Email Placement Chart */}
      <div className="relative">
        {isNotCompleted() && (
          <div className="absolute top-0 left-0 right-0 z-10 py-2 px-4 text-xs font-medium flex items-center justify-center shadow-sm bg-amber-50/90 dark:bg-amber-900/80 text-amber-700 dark:text-amber-400 border-b border-amber-200 dark:border-amber-800 rounded-t-lg backdrop-blur-sm shine-animation">
            <div className="flex space-x-2 mr-3">
              <div className="h-3 w-3 bg-amber-500 rounded-full animate-dot-pulse"></div>
              <div className="h-3 w-3 bg-amber-500 rounded-full animate-dot-pulse delay-150"></div>
              <div className="h-3 w-3 bg-amber-500 rounded-full animate-dot-pulse delay-300"></div>
            </div>
            <span className="font-medium">
              Retrieving data from email providers
            </span>
          </div>
        )}

        {providerBreakdownLoading ? (
          <Card>
            <CardContent className="p-4">
              <Skeleton className="h-8 w-64 mb-4" />
              <Skeleton className="h-[300px] w-full" />
            </CardContent>
          </Card>
        ) : (
          <EmailPlacementChart chartData={chartData} />
        )}
      </div>
    </div>
  );
};

const OverviewCardSkeleton = () => {
  return Array(3)
    .fill(0)
    .map((_, index) => (
      <Card key={index} className="relative">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Skeleton className="size-8 rounded-lg" />
                <Skeleton className="h-5 w-20" />
              </div>
              <div className="space-y-1">
                <Skeleton className="h-9 w-32" />
              </div>
            </div>
            <Skeleton className="size-20 rounded-full" />
          </div>
        </CardContent>
      </Card>
    ));
};

const ProgressCircle = ({
  percentage,
  color,
  className,
  strokeWidth = 8,
  textClassName,
}: {
  percentage: number;
  color: string;
  size?: number;
  className?: string;
  textClassName?: string;
  strokeWidth?: number;
}) => {
  const circumference = 289; // 2 * π * 46 (radius)
  const strokeDasharray = (percentage / 100) * circumference;

  return (
    <div className={cn("relative", className)}>
      {/* Background Circle */}
      <svg className="absolute w-full h-full -rotate-90" viewBox="0 0 100 100">
        <circle
          className="stroke-gray-200 dark:stroke-zinc-800"
          cx="50"
          cy="50"
          r="46"
          fill="none"
          strokeWidth={strokeWidth}
        />
      </svg>
      {/* Progress Circle */}
      <svg className="absolute w-full h-full -rotate-90" viewBox="0 0 100 100">
        <circle
          className={`transition-all duration-1000 ease-in-out ${color}`}
          cx="50"
          cy="50"
          r="46"
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${strokeDasharray} ${circumference}`}
        />
      </svg>
      <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
        <span
          className={cn(
            "text-sm font-bold text-muted-foreground",
            textClassName
          )}
        >
          {percentage.toFixed(1)}%
        </span>
      </div>
    </div>
  );
};

export default PlacementOverallScore;
